// Applies staged character changes and serializes the current roster from the DOM.
import { setCharacterBoolean, syncCharacterScreenState } from './character_screen_state';

export class OnscreenCharacterRoster {
  constructor(controller) {
    this.controller = controller;
  }

  change(event) {
    const character = event.currentTarget.closest('.character-tile');
    const attribute = event.currentTarget.dataset.characterScreenAttribute;
    const value = event.currentTarget.value === 'true';

    if (attribute === 'on_screen') this.#changeOnScreen(character, value);
    if (attribute === 'hidden') this.#changeHidden(character, value);
    if (attribute === 'active_turn') this.#changeActive(character, value);

    const snapshot = this.synchronize();
    if (attribute === 'on_screen') this.#focusAfterMove(character, value);
    return snapshot;
  }

  synchronize() {
    const onscreen = this.#entriesIn(this.controller.onScreenListTarget, true);
    const offscreen = this.#entriesIn(this.controller.offScreenListTarget, false);

    this.controller.onScreenEmptyTarget.classList.toggle(this.controller.hiddenClass, onscreen.length > 0);
    this.filter();
    return JSON.stringify([...onscreen, ...offscreen.sort((left, right) => left.id - right.id)]);
  }

  filter(event) {
    event?.preventDefault();
    const characters = this.#offscreenCharacters;
    const query = this.controller.queryTarget.value.trim().toLocaleLowerCase();
    let matches = 0;

    characters.forEach((character) => {
      const match = character.dataset.onscreenCharacterManagerFilterKey.toLocaleLowerCase().includes(query);
      character.classList.toggle(this.controller.hiddenClass, !match);
      if (match) matches += 1;
    });

    this.controller.offScreenEmptyTarget.classList.toggle(this.controller.hiddenClass, characters.length > 0);
    this.controller.noMatchesTarget.classList.toggle(
      this.controller.hiddenClass,
      query.length === 0 || characters.length === 0 || matches > 0
    );
  }

  clear(event) {
    event.preventDefault();
    this.controller.queryTarget.value = '';
    this.filter();
    this.controller.queryTarget.focus();
  }

  #changeOnScreen(character, onScreen) {
    character.classList.toggle('character-tile--just-added', onScreen);
    if (onScreen) {
      syncCharacterScreenState(character, true);
      character.classList.remove(this.controller.hiddenClass);
      this.controller.onScreenListTarget.append(character);
    } else {
      setCharacterBoolean(character, 'active_turn', false);
      character.dataset.characterScreenState = 'offscreen';
      this.controller.offScreenListTarget.append(character);
    }
  }

  #changeHidden(character, hidden) {
    character.dataset.characterHidden = String(hidden);
    if (hidden) setCharacterBoolean(character, 'active_turn', false);
    syncCharacterScreenState(character);
  }

  #changeActive(character, active) {
    if (!active) {
      character.dataset.characterActive = 'false';
      return;
    }

    this.controller.characterTargets.forEach((tile) => {
      setCharacterBoolean(tile, 'active_turn', tile === character);
    });
    setCharacterBoolean(character, 'hidden', false);
    syncCharacterScreenState(character);
  }

  #entriesIn(list, onScreen) {
    return this.controller.characterTargets
      .filter((character) => character.parentElement === list)
      .map((character, index) => {
        character.querySelector('[data-onscreen-character-manager-target~="position"]').value = onScreen
          ? index + 1
          : '';

        return {
          id: Number(character.dataset.id),
          visibility: character.dataset.characterScreenState,
          hidden: character.dataset.characterHidden === 'true',
          active: character.dataset.characterActive === 'true'
        };
      });
  }

  #focusAfterMove(character, onScreen) {
    const addButton = character.querySelector('.character-tile__screen-add').control;
    const fallback = character.classList.contains(this.controller.hiddenClass)
      ? this.controller.fallbackFocusTarget
      : addButton;
    (onScreen ? character.querySelector('.character-tile__sort-handle') : fallback).focus({ preventScroll: !onScreen });
  }

  get #offscreenCharacters() {
    return Array.from(this.controller.offScreenListTarget.children).filter(
      (item) => item.dataset.onscreenCharacterManagerFilterKey
    );
  }
}
